import { useState } from 'react';
import { workGenres } from '@/mocks/works';

export type SortKey = 'latest' | 'hot';

interface FilterBarProps {
  type: string;
  year: string;
  genre: string;
  sort: SortKey;
  onType: (v: string) => void;
  onYear: (v: string) => void;
  onGenre: (v: string) => void;
  onSort: (v: SortKey) => void;
  onReset: () => void;
  years: number[];
}

const typeOptions = ['全部', '电影', '电视剧'];
const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '热门' },
];

export default function FilterBar({
  type,
  year,
  genre,
  sort,
  onType,
  onYear,
  onGenre,
  onSort,
  onReset,
  years,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const selectClass =
    'text-sm rounded-md border border-background-300 bg-background-50 text-foreground-800 px-3 py-2 outline-none focus:border-primary-400 cursor-pointer';

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between md:hidden mb-3">
        <span className="text-sm text-foreground-600">
          <i className="ri-filter-3-line mr-1" />
          筛选条件
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap cursor-pointer"
        >
          {open ? '收起筛选' : '展开筛选'}
        </button>
      </div>

      <div
        className={`${open ? 'flex' : 'hidden'} md:flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-background-100 border border-background-200/80 rounded-lg p-4`}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 whitespace-nowrap">类型</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-200/70">
              {typeOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onType(opt)}
                  className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    type === opt
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-700 hover:text-primary-600'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 whitespace-nowrap">年份</span>
            <select value={year} onChange={(e) => onYear(e.target.value)} className={selectClass}>
              <option value="全部">全部年份</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y} 年
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 whitespace-nowrap">题材</span>
            <select value={genre} onChange={(e) => onGenre(e.target.value)} className={selectClass}>
              <option value="全部">全部题材</option>
              {workGenres.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-4">
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-200/70">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onSort(opt.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  sort === opt.key
                    ? 'bg-accent-500 text-background-50'
                    : 'text-foreground-700 hover:text-accent-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置
          </button>
        </div>
      </div>
    </div>
  );
}